import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { IconDotsVertical, IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import DeleteVideoDialog from "./DeleteVideoDialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import Toast from "@/lib/components/Toast";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { useNavigate, useParams } from "react-router";
import { useIsMobile } from "@/lib/use-mobile";
import { AnimatedImageApi } from "@/lib/api/animated-image-api";
import { paths } from "@/App";
import { useDuplicateVideoMutation } from "@/lib/queries/video-queries";
import { useFeatureToggleControls } from "@/lib/feature-flags";
import { Switch } from "@/lib/components/Switch";

type DialogType = "delete-video" | null;

export default function VideoSettingsDropdown() {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const setIsImageSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsImageSelectorDialogOpen,
  );
  const setIsCreditDialogOpen = useEditorStore(
    (state) => state.service.setIsCreditDialogOpen,
  );

  const video = useEditorStore((state) => state.video);
  const currentTeamMembership = useCurrentTeamMembership();

  const {
    canToggle: canToggleFeatures,
    enabled: featuresEnabled,
    setEnabled: setFeaturesEnabled,
  } = useFeatureToggleControls();

  const duplicateVideoMutation = useDuplicateVideoMutation({
    teamExternalId: teamExternalId!,
    onSuccess: (newVideo) => {
      setOpen(false);
      navigate(paths.videoEdit(teamExternalId!, newVideo.externalId));
    },
  });

  // Check if the video has any animated images in the composition
  const hasAnimatedImages =
    video?.compositionJson?.mainSceneTimeline?.some(
      (item) => item.animatedImageExternalId,
    ) ?? false;

  const handleBulkDownload = async () => {
    if (!currentTeamMembership || !video || !hasAnimatedImages) return;

    const animatedImageExternalIds = video.compositionJson.mainSceneTimeline
      .filter((item) => item.animatedImageExternalId)
      .map((item) => item.animatedImageExternalId!);

    setIsDownloading(true);
    setOpen(false);

    try {
      await AnimatedImageApi.bulkDownloadAnimatedImages(
        currentTeamMembership.teamExternalId,
        { animatedImageExternalIds },
      );
    } catch {
      Toast.error("Failed to download animated images. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleManageImagesClick = () => {
    setOpen(false);
    setIsImageSelectorDialogOpen(true);
  };

  if (isMobile) {
    return null;
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <IconDotsVertical size={20} className="text-text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-60"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {team?.billingInfo.showCredits && (
            <DropdownMenuItem onClick={() => setIsCreditDialogOpen(true)}>
              Add credits
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleManageImagesClick}>
            Browse images & animations
          </DropdownMenuItem>
          {hasAnimatedImages && (
            <DropdownMenuItem
              onClick={handleBulkDownload}
              disabled={isDownloading}
              onPointerLeave={(event) => event.preventDefault()}
            >
              {isDownloading ? "Downloading..." : "Download original clips"}
              <IconDownload
                size={18}
                strokeWidth={1.5}
                className="text-text-primary"
              />
            </DropdownMenuItem>
          )}
          {video && (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                duplicateVideoMutation.mutate(video.externalId);
              }}
              disabled={duplicateVideoMutation.isPending}
              onPointerLeave={(event) => event.preventDefault()}
            >
              {duplicateVideoMutation.isPending
                ? "Duplicating..."
                : "Duplicate video"}
            </DropdownMenuItem>
          )}
          {canToggleFeatures && (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                setFeaturesEnabled(!featuresEnabled);
              }}
            >
              Admin mode
              <Switch
                checked={featuresEnabled}
                className="pointer-events-none ml-auto"
                tabIndex={-1}
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-error hover:text-error!"
            onSelect={() => setOpenDialog("delete-video")}
          >
            Delete video
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteVideoDialog
        open={openDialog === "delete-video"}
        onOpenChange={(isOpen) => !isOpen && setOpenDialog(null)}
      />
    </>
  );
}
